// React Hooks for Processes
// Part of Policy Framework & EU Diseases Implementation

import { useState, useEffect } from 'react';
import processService from '../services/processService';

/**
 * Hook to fetch all processes
 */
export function useProcesses(organizationId, filters = {}) {
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadProcesses = async () => {
    if (!organizationId) {
      setProcesses([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await processService.getProcesses(organizationId, filters);
      setProcesses(data);
    } catch (err) {
      console.error('Error loading processes:', err);
      setError(err.message);
      setProcesses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProcesses();
  }, [organizationId, JSON.stringify(filters)]);

  const refresh = () => {
    loadProcesses();
  };

  return { processes, loading, error, refresh };
}

/**
 * Hook to fetch single process
 */
export function useProcess(processId) {
  const [process, setProcess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadProcess = async () => {
    if (!processId) {
      setProcess(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await processService.getProcess(processId);
      setProcess(data);
    } catch (err) {
      console.error('Error loading process:', err);
      setError(err.message);
      setProcess(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProcess();
  }, [processId]);

  const refresh = () => {
    loadProcess();
  };

  return { process, loading, error, refresh };
}

/**
 * Hook for process mutations
 */
export function useProcessMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createProcess = async (data, userId, userName) => {
    try {
      setLoading(true);
      setError(null);
      const id = await processService.createProcess(data, userId, userName);
      return id;
    } catch (err) {
      console.error('Error creating process:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateProcess = async (processId, updates, userId, userName) => {
    try {
      setLoading(true);
      setError(null);
      await processService.updateProcess(processId, updates, userId, userName);
    } catch (err) {
      console.error('Error updating process:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteProcess = async (processId, userId, userName) => {
    try {
      setLoading(true);
      setError(null);
      await processService.deleteProcess(processId, userId, userName);
    } catch (err) {
      console.error('Error deleting process:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const linkProtocol = async (
    processId,
    protocolId,
    protocolName,
    linkType,
    implementsStep,
    userId,
    userName
  ) => {
    try {
      setLoading(true);
      setError(null);
      await processService.linkProtocol(
        processId,
        protocolId,
        protocolName,
        linkType,
        implementsStep,
        userId,
        userName
      );
    } catch (err) {
      console.error('Error linking protocol:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    createProcess,
    updateProcess,
    deleteProcess,
    linkProtocol,
    loading,
    error
  };
}

